import React, { useState, useRef, useEffect } from 'react';
import { useGetMediaAssetsQuery, useUploadMediaAssetMutation } from '../store/services/whatsappApi';
import { MediaAsset } from '../types/mediaAsset';

interface MediaAssetsProps {
  onSelectMedia: (mediaHandle: string, fileType: string, mediaUrl?: string) => void;
  allowedType?: 'image' | 'video' | 'document';
  requireResumableHandle?: boolean; // when true, only show assets whose media_handle is non-numeric (Meta Resumable Upload handle)
}

const MediaAssets: React.FC<MediaAssetsProps> = ({ onSelectMedia, allowedType, requireResumableHandle = false }) => {
  const [uploadType, setUploadType] = useState<string>(allowedType || 'image');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  
  // Keep uploadType in sync with allowedType from parent (e.g., header format)
  useEffect(() => {
    if (allowedType) {
      setUploadType(allowedType);
    }
  }, [allowedType]);
  
  // API hooks
  const { data: mediaAssets, isLoading, refetch } = useGetMediaAssetsQuery();
  const [uploadMediaAsset] = useUploadMediaAssetMutation();
  
  // WhatsApp supported file types (strict validation)
  const validateFileType = (file: File): boolean => {
    // WhatsApp only supports these specific types
    const imageTypes = ['image/jpeg', 'image/png'];
    const videoTypes = ['video/mp4', 'video/3gpp'];
    const documentTypes = [
      'application/pdf',
      'application/vnd.ms-powerpoint',
      'application/msword',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    let validTypes: string[] = [];
    
    switch (uploadType) {
      case 'image':
        validTypes = imageTypes;
        break;
      case 'video':
        validTypes = videoTypes;
        break;
      case 'document':
        validTypes = documentTypes;
        break;
    }
    
    return validTypes.includes(file.type);
  };
  
  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    // Validate file type
    if (!validateFileType(file)) {
      let supportedFormats = '';
      if (uploadType === 'image') supportedFormats = 'JPEG, PNG';
      else if (uploadType === 'video') supportedFormats = 'MP4, 3GPP';
      else if (uploadType === 'document') supportedFormats = 'PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX';
      
      setUploadError(`Unsupported file type: ${file.type}. WhatsApp only supports ${supportedFormats} for ${uploadType}s.`);
      return;
    }
    
    // Validate file size based on type (WhatsApp limits)
    const maxSizes = {
      image: 5 * 1024 * 1024,      // 5MB
      video: 16 * 1024 * 1024,     // 16MB
      document: 100 * 1024 * 1024  // 100MB
    };
    
    const maxSize = maxSizes[uploadType as keyof typeof maxSizes] || 16 * 1024 * 1024;
    if (file.size > maxSize) {
      const sizeMB = (maxSize / (1024 * 1024)).toFixed(0);
      setUploadError(`File too large! Maximum size for ${uploadType} is ${sizeMB}MB. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`);
      return;
    }
    
    setUploadError('');
    setIsUploading(true);
    
    try {
      // Create form data for upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', uploadType);
      
      // Upload the file
      await uploadMediaAsset(formData).unwrap();
      
      // Refetch media assets
      refetch();
      
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading media asset:', error);
      setUploadError('Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle media selection
  const handleSelectMedia = (asset: MediaAsset) => {
    onSelectMedia(asset.media_handle, asset.file_type, asset.original_url);
  };

  const isResumableHandle = (handle?: string) => {
    if (!handle) return false;
    // WhatsApp Business API media IDs are numeric; resumable upload handles are non-numeric strings (often contain colons)
    return !/^\d+$/.test(handle);
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Media Assets</h2>
        {allowedType && (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
            {allowedType.toUpperCase()}
          </span>
        )}
      </div>
      
      {/* Upload Section */}
      <div className="mb-6">
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Upload New Media
          </label>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <div className="relative">
              <select 
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value)}
                disabled={!!allowedType}
                className={`mr-2 shadow border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${allowedType ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                title={allowedType ? `Restricted to ${allowedType}` : 'Select media type'}
              >
                <option value="image">Image</option>
                <option value="video">Video</option>
                <option value="document">Document</option>
              </select>
            </div>
            <label className="inline-flex items-center bg-whatsapp-green hover:bg-whatsapp-dark-green text-white py-2 px-4 rounded cursor-pointer">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Select File
              <input 
                ref={fileInputRef}
                type="file" 
                className="hidden" 
                onChange={handleFileSelect}
                accept={
                  uploadType === 'image' 
                    ? 'image/jpeg,image/png' 
                    : uploadType === 'video' 
                    ? 'video/mp4,video/3gpp' 
                    : 'application/pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx'
                }
              />
            </label>
            <span className="text-xs text-gray-500">
              Max: {uploadType === 'image' ? '5MB' : uploadType === 'video' ? '16MB' : '100MB'}
            </span>
          </div>
          {isUploading && (
            <div className="flex items-center mt-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-whatsapp-green mr-2"></div>
              <span className="text-sm text-gray-600">Uploading...</span>
            </div>
          )}
          {uploadError && (
            <p className="text-red-500 text-xs mt-1">{uploadError}</p>
          )}
        </div>
      </div>
      
      {/* Media Gallery */}
      <div>
        <h3 className="text-md font-medium mb-3">Your Media Assets</h3>
        
        {isLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-whatsapp-green"></div>
          </div>
        ) : !mediaAssets || mediaAssets.length === 0 ? (
          <div className="bg-gray-50 p-4 text-center rounded">
            <p className="text-gray-500">No media assets found. Upload your first media file.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-60 overflow-y-auto p-1">
            {mediaAssets
              .filter((asset: MediaAsset) => !allowedType || asset.file_type === allowedType)
              .filter((asset: MediaAsset) => !requireResumableHandle || isResumableHandle(asset.media_handle))
              .map((asset: MediaAsset) => (
              <div 
                key={asset.id} 
                className="border rounded-md p-2 cursor-pointer hover:border-whatsapp-green transition-colors group"
                onClick={() => handleSelectMedia(asset)}
              >
                {asset.file_type === 'image' ? (
                  <div className="aspect-square overflow-hidden bg-gray-100 rounded mb-1">
                    <img 
                      src={asset.original_url || '/placeholder-image.png'} 
                      alt={asset.file_name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : asset.file_type === 'video' ? (
                  <div className="aspect-square overflow-hidden bg-gray-100 rounded flex items-center justify-center mb-1">
                    {asset.original_url ? (
                      <video 
                        src={asset.original_url} 
                        className="w-full h-full object-cover"
                        muted
                        playsInline
                      />
                    ) : (
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </div>
                ) : (
                  <div className="aspect-square overflow-hidden bg-gray-100 rounded flex items-center justify-center mb-1">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <p className="text-xs truncate" title={asset.file_name}>{asset.file_name}</p>
                  <span className="text-[10px] px-1 py-0.5 rounded bg-gray-100 text-gray-600 ml-1 capitalize">{asset.file_type}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaAssets;
